/// <reference path="../../common/common.module.ts" />
/// <reference path="webComponentRegistry.service.ts" />
/// <reference path="webComponentLoaderFactory.service.ts" />
/// <reference path="../directives/webComponentMount.directive.ts" />

/**
 * Web Component Setup Service
 *
 * Single-call registration that wires up everything a web component needs:
 *   - Bundle loader (auto-generated via webComponentLoaderFactory)
 *   - Feature gate source resolver
 *   - Post-mount event hooks
 *
 * Usage:
 *   angular.module('YourModule').run(['webComponentSetup', function (webComponentSetup) {
 *       webComponentSetup.register({
 *           id: 'your-feature',
 *           // All other fields are optional — conventions apply:
 *           //   tag:           'meevo-your-feature'
 *           //   scriptPath:    '/lib/meevo-your-feature.js'
 *           //   loaderService: 'yourFeatureWcLoader'
 *       });
 *   }]);
 */

interface WebComponentSetupConfig {
    /** Enrollment id — must match an entry in webComponentRegistry.json */
    id: string;
    /** Custom element tag. Default: 'meevo-' + id */
    tag?: string;
    /** Script bundle path. Default: '/lib/<tag>.js' */
    scriptPath?: string;
    /**
     * Gate source name — must match featureGate.source in webComponentRegistry.json.
     * Required when gateResolver is provided.
     */
    gateSource?: string;
    /** Resolver function that returns the gate source data. Paired with gateSource. */
    gateResolver?: () => any;
    /** Post-mount callback(s). Runs after element is appended to DOM. */
    onMount?: ((element: HTMLElement) => void) | Array<(element: HTMLElement) => void>;
    /**
     * Pre-unmount callback(s). Runs just before the element is removed from
     * the DOM (on route change / scope destroy). Use this to tear down
     * *host-side* side effects wired up in onMount — e.g., $rootScope.$on
     * return values, window listeners, or bridge subscriptions held by the
     * host. Element-scoped addEventListener calls do NOT need an explicit
     * unmount hook (they die with the element).
     */
    onUnmount?: ((element: HTMLElement) => void) | Array<(element: HTMLElement) => void>;
    /**
     * Callback returning properties to set on the custom element BEFORE it is
     * appended to the DOM. Called once per mount; cleared after consumption.
     * Use this to pass initial state that should be available during the
     * component's first initialization cycle.
     */
    preMountProps?: () => { [key: string]: any } | null;
    /** Module to register the loader on. Default: 'meevo.components.common' */
    loaderModule?: string;
    /** Override the auto-derived loader service name */
    loaderServiceName?: string;
}

angular.module('meevo.components.common').factory('webComponentSetup', [
    'webComponentRegistry',
    'webComponentLoaderFactory',
    'webComponentMountHooks',
    function (
        webComponentRegistry: any,
        webComponentLoaderFactory: any,
        webComponentMountHooks: any,
    ) {
        var registered: { [id: string]: boolean } = {};

        function register(config: WebComponentSetupConfig): void {
            if (!config || !config.id) {
                console.error('[webComponentSetup] config.id is required.');
                return;
            }

            if (registered[config.id]) {
                console.warn('[webComponentSetup] "' + config.id + '" already registered. Skipping.');
                return;
            }
            registered[config.id] = true;

            var tag = config.tag || ('meevo-' + config.id);
            var loaderModule = config.loaderModule || 'meevo.components.common';

            // 1. Register loader
            webComponentLoaderFactory.registerLoader(
                loaderModule,
                tag,
                config.scriptPath,
                config.loaderServiceName
            );

            // 2. Register gate source
            if (config.gateSource && config.gateResolver) {
                webComponentRegistry.registerGateSource(config.gateSource, config.gateResolver);
            }

            // 3. Register post-mount hooks
            if (config.onMount) {
                var hooks = Array.isArray(config.onMount) ? config.onMount : [config.onMount];
                for (var i = 0; i < hooks.length; i++) {
                    webComponentMountHooks.register(config.id, hooks[i]);
                }
            }

            // 3b. Register pre-unmount hooks
            if (config.onUnmount) {
                var unmountHooks = Array.isArray(config.onUnmount) ? config.onUnmount : [config.onUnmount];
                for (var j = 0; j < unmountHooks.length; j++) {
                    webComponentMountHooks.registerUnmount(config.id, unmountHooks[j]);
                }
            }

            // 4. Register pre-mount props callback
            if (config.preMountProps) {
                webComponentMountHooks.registerPreMountProps(config.id, config.preMountProps);
            }

            // 5. Validate enrollment exists (async, warning only)
            webComponentRegistry.getEnrollment(config.id).catch(function () {
                console.warn(
                    '[webComponentSetup] No enrollment found for "' + config.id +
                    '" in webComponentRegistry.json. The component will not mount until an entry is added.'
                );
            });

            console.log('[webComponentSetup] Registered "' + config.id + '" (tag: ' + tag + ')');
        }

        return { register: register };
    }
]);
